/**
 * Live TLE refresh, ported from loader-catalog.js refreshLiveTLEs and the
 * present-time detection in loader-app.js (_isNearPresent). Every dataset
 * mission with a NORAD id (deep-space missions excepted) is matched by identity
 * against a shared public active catalogue. The CATNR injection is retained
 * for callers/tests that explicitly supply a JSON fetcher.
 */
import { toast } from '@/components/ui/toast';
import { clockNow } from '@/state/store';
import { fetchTleCatalog, tleEpochAgeDays, type TleCatalogResult } from '../engine';
import { loaderApi } from '../data/api';
import { useLoader } from '../store';
import type { Mission } from '../types';
import { fetchByCatnr, ommTle, type CelestrakDeps } from './celestrak';

/** Feature toggle id that gates the refresh (unknown ids count as enabled). */
export const LIVE_TLE_TOGGLE_ID = 'live_tle_refresh';
/** "Present" means the shared clock is within this distance of wall time (24 h, as the original). */
export const PRESENT_WINDOW_MS = 86_400_000;
/** Refresh cadence while present (2 h). */
export const REFRESH_INTERVAL_MS = 7_200_000;
/** How often the runtime re-evaluates the clock and dataset. */
export const CHECK_INTERVAL_MS = 1_000;
/**
 * Parallel CelesTrak requests during one refresh. Four, not more: over HTTP/1.1
 * a browser opens six connections per host, and a refresh whose upstream hangs
 * must leave room for the page's own module and API requests.
 */
export const REFRESH_CONCURRENCY = 4;

export function isNearPresent(clockMs: number, nowMs: number): boolean {
  return Math.abs(clockMs - nowMs) < PRESENT_WINDOW_MS;
}

/**
 * Whether the refresh may run now: the shared clock at present time, the toggle
 * on, and a dataset from D1 or the bundled JSON (live constellations come from
 * TLE feeds already and are not refreshed here).
 */
export function liveTleEligible(): boolean {
  const st = useLoader.getState();
  const dataset = st.dataset;
  if (!dataset || dataset.network === 'full_catalogue' || dataset.source === 'live') return false;
  if (!(st.toggles[LIVE_TLE_TOGGLE_ID] ?? true)) return false;
  return isNearPresent(clockNow(), Date.now());
}

/** Missions the refresh touches: a NORAD id and not deep space (as the original). */
export function isRefreshable(mission: Mission): boolean {
  return (
    Boolean(mission.noradId) && !['deep_space', 'l1', 'lunar'].includes(mission.orbit?.type.toLowerCase())
  );
}

export interface RefreshOutcome {
  /** The input list with refreshed missions replaced (same order). */
  missions: Mission[];
  updated: number;
  failed: { id: string; reason: string }[];
  /** How many missions qualified for a refresh. */
  attempted: number;
  source?: string;
}

export interface RefreshDeps extends CelestrakDeps {
  concurrency?: number;
  catalog?: () => Promise<TleCatalogResult>;
}

/**
 * Fetches a fresh TLE per refreshable mission. Failures are collected rather
 * than thrown so one unreachable satellite does not stop the rest; missions
 * that fail keep their current TLE.
 */
export async function refreshMissionTles(
  missions: Mission[],
  deps: RefreshDeps = {},
): Promise<RefreshOutcome> {
  const now = deps.now ?? Date.now;
  const concurrency = Math.max(1, deps.concurrency ?? REFRESH_CONCURRENCY);
  const result = missions.slice();
  const failed: RefreshOutcome['failed'] = [];
  let updated = 0;

  // Curated networks previously made dozens of individual JSON requests; those
  // had no mirror fallback. One cached catalogue shares the validated feed used
  // by live constellations, without changing mission requirements or antenna choices.
  if (!deps.fetchJson || deps.catalog) {
    let catalog: TleCatalogResult;
    try {
      catalog = await (deps.catalog ?? (() => fetchTleCatalog(loaderApi.celestrakUrl('active'))))();
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      const targets = missions.filter(isRefreshable);
      return {
        missions,
        updated: 0,
        attempted: targets.length,
        failed: targets.map((m) => ({ id: m.id, reason })),
      };
    }
    let attempted = 0;
    for (const [index, mission] of missions.entries()) {
      if (!isRefreshable(mission)) continue;
      attempted++;
      // A unique exact mission-name/approved-alias match also repairs wrong
      // seed NORAD ids. Duplicate seed numbers alone must not override identity.
      const matches = catalog.satellites.filter((s) => catalogIdentityMatches(mission, s.name));
      const sat = matches.length === 1 ? matches[0] : undefined;
      if (!sat) {
        const reason = matches.length
          ? 'Catalogue identity is ambiguous'
          : 'No matching spacecraft in the active catalogue';
        failed.push({ id: mission.id, reason });
        result[index] = {
          ...mission,
          orbit: { ...mission.orbit, tleStatus: 'unverified', tleIssue: reason },
        };
        continue;
      }
      const age = tleEpochAgeDays(sat.tle1, now());
      if (age > 14 || age < -2) {
        failed.push({ id: mission.id, reason: 'Public elements are stale' });
        continue;
      }
      const old = mission.orbit.tle;
      if (old && String(mission.noradId) === String(sat.noradId) && age > tleEpochAgeDays(old.line1, now()))
        continue;
      result[index] = {
        ...mission,
        noradId: sat.noradId,
        orbit: {
          ...mission.orbit,
          tle: { line1: sat.tle1, line2: sat.tle2 },
          tleEpochAgeDays: age,
          tleSource: catalog.source ?? 'CelesTrak',
          tleStatus: 'verified',
          tleIssue: undefined,
        },
      };
      updated++;
    }
    return { missions: result, updated, failed, attempted, source: catalog.source };
  }

  const targets = missions
    .map((mission, index) => ({ mission, index }))
    .filter(({ mission }) => isRefreshable(mission));

  let cursor = 0;
  const worker = async () => {
    while (cursor < targets.length) {
      const { mission, index } = targets[cursor++];
      try {
        const omm = await fetchByCatnr(mission.noradId as number | string, deps);
        if (!omm) {
          failed.push({ id: mission.id, reason: `no GP data for NORAD ${mission.noradId}` });
          continue;
        }
        const tle = ommTle(omm);
        if (!tle) {
          failed.push({
            id: mission.id,
            reason: `GP record for NORAD ${mission.noradId} has no usable epoch`,
          });
          continue;
        }
        result[index] = {
          ...mission,
          orbit: { ...mission.orbit, tle, tleEpochAgeDays: tleEpochAgeDays(tle.line1, now()) },
        };
        updated++;
      } catch (err: unknown) {
        failed.push({ id: mission.id, reason: err instanceof Error ? err.message : String(err) });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, targets.length) }, worker));

  return { missions: result, updated, failed, attempted: targets.length };
}

/**
 * Refreshes the loader dataset in place: patches the missions (keeping the
 * what-if selection) and reports "Refreshed N TLEs" or the failure as a toast.
 * Skips silently when no dataset is loaded or it changed during the fetch.
 */
/** A total failure (CelesTrak unreachable) is toasted once per session; later ones only reach the console. */
let totalFailureToasted = false;

export async function runLiveTleRefresh(deps: RefreshDeps = {}): Promise<RefreshOutcome | null> {
  const loader = useLoader.getState();
  const dataset = loader.dataset;
  if (!dataset || dataset.network === 'full_catalogue') return null;

  const outcome = await refreshMissionTles(dataset.missions, deps);
  if (outcome.attempted === 0) return outcome;

  const current = useLoader.getState();
  if (current.dataset !== dataset) return outcome;

  if (outcome.missions.some((m, i) => m !== dataset.missions[i])) {
    const active = current.activeMissionIds;
    current.patchDataset({
      missions: outcome.missions,
      ...(outcome.source ? { catalogSources: [outcome.source] } : {}),
    });
    useLoader.getState().setActiveMissionIds(active);
  }

  const first = outcome.failed[0];
  if (outcome.failed.length === 0) {
    toast('success', `Refreshed ${outcome.updated} TLE${outcome.updated === 1 ? '' : 's'}`);
  } else if (outcome.updated === 0) {
    console.error('[live-tle] refresh failed:', outcome.failed);
    if (!totalFailureToasted) {
      totalFailureToasted = true;
      toast(
        'error',
        `TLE refresh failed for ${outcome.failed.length} mission${outcome.failed.length === 1 ? '' : 's'}: ${first.id} (${first.reason})`,
      );
    }
  } else {
    console.error('[live-tle] partial refresh:', outcome.failed);
    toast(
      'warn',
      `Refreshed ${outcome.updated} TLE${outcome.updated === 1 ? '' : 's'}; ${outcome.failed.length} failed: ${first.id} (${first.reason})`,
    );
  }
  return outcome;
}

/** The seed contains planning placeholders and some reused NORAD numbers. Do
 * not silently put the orbit of an unrelated spacecraft under their labels. */
export function catalogIdentityMatches(
  mission: Pick<Mission, 'id' | 'name' | 'shortName'>,
  name: string,
): boolean {
  const normal = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const actual = normal(name);
  const aliases: Record<string, string[]> = {
    suomi_npp: ['SUOMINPP', 'NPP'],
    noaa20: ['NOAA20', 'JPSS1'],
    noaa21: ['NOAA21', 'JPSS2'],
    metop_b: ['METOPB'],
    metop_c: ['METOPC'],
    metop_sg_a1: ['METOPSGA1'],
    dmsp_f16: ['DMSP5DF16', 'DMSP5D3F16'],
    dmsp_f17: ['DMSP5DF17', 'DMSP5D3F17'],
    dmsp_f18: ['DMSP5DF18', 'DMSP5D3F18'],
    ews_g2: ['EWSG2GOES15', 'GOES15'],
    ews_g3: ['EWSG3GOES14', 'GOES14'],
    gazelle: ['OTB3GAZELLE'],
    cosmic2_1: ['FORMOSAT71'], cosmic2_2: ['FORMOSAT72'], cosmic2_3: ['FORMOSAT73'],
    cosmic2_4: ['FORMOSAT74'], cosmic2_5: ['FORMOSAT75'], cosmic2_6: ['FORMOSAT76'],
    geo_kompsat_2a: ['GEOKOMPSAT2A', 'GK2A'],
    geo_kompsat_2b: ['GEOKOMPSAT2B', 'GK2B'],
    kompsat2: ['ARIRANG2KOMPSAT2'],
    kompsat3: ['ARIRANG3KOMPSAT3'],
    kompsat5: ['ARIRANG5KOMPSAT5'],
    iss: ['ISSZARYA'],
    hubble: ['HST'],
    gpm: ['GPMCORE'],
    sentinel6: ['SENTINEL6A'],
    gracefo: ['GRACEFO1'],
    sage3: ['ISSZARYA'],
    clarreo: ['ISSZARYA'],
    emit: ['ISSZARYA'],
    ecostress: ['ISSZARYA'],
    oco3: ['ISSZARYA'],
    gedi: ['ISSZARYA'],
    wgs_7: ['WGSF7'],
    wgs_8: ['WGSF8'],
    wgs_9: ['WGSF9'],
    wgs_10: ['WGSF10'],
    goes18_milsupport: ['GOES18'],
  };
  return [
    ...(aliases[mission.id] ?? []),
    normal(mission.id),
    normal(mission.shortName),
    normal(mission.name.split('(')[0]),
  ].some(
    (alias) =>
      alias.length >= 3 &&
      (actual === alias || actual.startsWith(alias + 'JPSS') || actual.startsWith(alias + 'USA')),
  );
}

/**
 * Scheduler key: one refresh cycle per dataset load. The key must not follow
 * the dataset object itself: every patch (a sync poll, a placed dish, the
 * refresh's own TLE patch) creates a new object and would start the cycle
 * again at once, so a reachable CelesTrak would be polled continuously.
 */
export function liveTleDatasetKey(): string | null {
  const s = useLoader.getState();
  return s.dataset ? `${s.network}:${s.datasetEpoch}` : null;
}

export interface LiveTleSchedulerOptions {
  /** Present, toggle on and a refreshable dataset source. */
  isEligible: () => boolean;
  /** Identity of the current dataset; a new value restarts the once-per-dataset cycle. */
  datasetKey: () => unknown;
  run: () => Promise<unknown>;
  intervalMs?: number;
  checkMs?: number;
  now?: () => number;
}

export interface LiveTleScheduler {
  start: () => void;
  stop: () => void;
  /** One evaluation step (the interval calls it; tests call it directly). */
  check: () => void;
  isRunning: () => boolean;
}

/**
 * Runs the refresh once when a dataset becomes eligible (present time, toggle
 * on), then every intervalMs while it stays eligible. Leaving present stops the
 * cadence; coming back within the interval does not refresh again. A run never
 * overlaps another.
 */
export function createLiveTleScheduler(options: LiveTleSchedulerOptions): LiveTleScheduler {
  const intervalMs = options.intervalMs ?? REFRESH_INTERVAL_MS;
  const checkMs = options.checkMs ?? CHECK_INTERVAL_MS;
  const now = options.now ?? Date.now;

  let timer: ReturnType<typeof setInterval> | null = null;
  let inFlight = false;
  let lastKey: unknown = undefined;
  let lastRunMs: number | null = null;

  const fire = () => {
    if (inFlight) return;
    inFlight = true;
    lastRunMs = now();
    let pending: Promise<unknown>;
    try {
      pending = Promise.resolve(options.run());
    } catch (err) {
      pending = Promise.reject(err);
    }
    pending
      .catch((err: unknown) => {
        console.error('[live-tle] refresh run failed:', err);
      })
      .finally(() => {
        inFlight = false;
      });
  };

  const check = () => {
    const key = options.datasetKey();
    if (key !== lastKey) {
      lastKey = key;
      lastRunMs = null;
    }
    if (!options.isEligible()) return;
    if (lastRunMs === null || now() - lastRunMs >= intervalMs) fire();
  };

  return {
    start: () => {
      if (timer !== null) return;
      check();
      timer = setInterval(check, checkMs);
    },
    stop: () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    },
    check,
    isRunning: () => inFlight,
  };
}

export interface LoaderLiveTleSchedulerDeps {
  /** A presenter freeze pauses the cadence without ending the cycle. */
  isFrozen?: () => boolean;
  refresh?: RefreshDeps;
  now?: () => number;
}

/** The runtime's scheduler: present-time eligibility minus a presenter freeze, one cycle per dataset load. */
export function createLoaderLiveTleScheduler(deps: LoaderLiveTleSchedulerDeps = {}): LiveTleScheduler {
  return createLiveTleScheduler({
    isEligible: () => liveTleEligible() && !(deps.isFrozen?.() ?? false),
    datasetKey: liveTleDatasetKey,
    run: () => runLiveTleRefresh(deps.refresh),
    now: deps.now,
  });
}
